'use client';

import { Button, Container, Stack, Text, Title } from '@mantine/core';
import React from 'react';
import { clientLogger } from '@/utils/logger';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const correlationId = `error-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    clientLogger.error('React Error Boundary caught error', {
      correlationId,
      error: {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      },
    });

    // In production, you would send this to your error tracking service
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container size="sm" py="xl">
          <Stack gap="md">
            <Title order={1}>Something went wrong</Title>
            <Text c="dimmed">
              We apologize for the inconvenience. An error has occurred while
              rendering this page.
            </Text>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Error Details:
                </Text>
                <Text size="xs" c="red" style={{ fontFamily: 'monospace' }}>
                  {this.state.error.message}
                </Text>
                {this.state.errorInfo && (
                  <details style={{ cursor: 'pointer' }}>
                    <summary>Component Stack</summary>
                    <pre style={{ fontSize: '11px', overflow: 'auto' }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </Stack>
            )}
            <Button onClick={this.handleReset} variant="outline">
              Try Again
            </Button>
          </Stack>
        </Container>
      );
    }

    return this.props.children;
  }
}
